import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { formatShortDate } from "@/lib/formatters";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  getJobStatus,
  fetchAllResults,
  submitFeedback,
  getJobFeedback,
  exportCSV,
} from "@/lib/api-client";
import { useCredits } from "@/contexts/CreditsContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { parseApiError } from "@/lib/api-error";
import { EmptyState } from "@/components/EmptyState";
import { type FeedbackState } from "@/components/FeedbackButtons";
import { InlineDetailExpansion } from "@/components/InlineDetailExpansion";
import { ResultCard } from "@/components/ResultCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft,
  Download,
  Search,
  MapPin,
  ChevronDown,
  ChevronUp,
  Building2,
  Radar,
  Copy,
  Calendar,
} from "lucide-react";
import RadarSpinner from "@/components/RadarSpinner";
import { ExpandSearchDialog } from "@/components/ExpandSearchDialog";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
} from "@/components/ui/pagination";
import type { CompanyResult, FeedbackReason, SearchJob } from "@/types/api";
import { buildSearchSummary } from "@/lib/buildSearchSummary";

const PAGE_SIZE = 25;

export default function ResultsPage() {
  const { t } = useTranslation("results");
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [job, setJob] = useState<SearchJob | null>(null);
  const [allResults, setAllResults] = useState<CompanyResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedResultId, setExpandedResultId] = useState<string | null>(null);
  const [feedbackState, setFeedbackState] = useState<
    Record<string, FeedbackState>
  >({});
  const [paramsOpen, setParamsOpen] = useState(false);
  const [expanding, setExpanding] = useState(false);
  const expandPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [csvExporting, setCsvExporting] = useState(false);
  const resultsAreaRef = useRef<HTMLDivElement>(null);
  const { costOf, refreshCredits } = useCredits();
  const { profile } = useAuth();

  // ── Pagination ─────────────────────────────────────────────────
  const currentPage = Math.max(
    1,
    parseInt(searchParams.get("page") ?? "1", 10) || 1,
  );

  const goToPage = useCallback(
    (page: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("page", String(page));
          return next;
        },
        { replace: true },
      );
      setExpandedResultId(null);
      resultsAreaRef.current?.scrollIntoView({ behavior: "smooth" });
    },
    [setSearchParams],
  );

  // Load existing feedback from the Railway API
  const loadFeedback = useCallback(async (jId: string) => {
    try {
      const data = await getJobFeedback(jId);
      const map: Record<string, FeedbackState> = {};
      for (const [resultId, fb] of Object.entries(data)) {
        map[resultId] = {
          signal: fb.signal as "relevant" | "not_relevant",
          reason: fb.reason ?? undefined,
        };
      }
      setFeedbackState(map);
    } catch (err) {
      console.error("Failed to load feedback:", err);
    }
  }, []);

  // Start polling for expansion completion
  const startExpansionPolling = useCallback(() => {
    if (!jobId) return;
    setExpanding(true);

    // Clear any existing poll
    if (expandPollRef.current) clearInterval(expandPollRef.current);

    expandPollRef.current = setInterval(async () => {
      try {
        const updated = await getJobStatus(jobId);
        setJob(updated);

        // Expansion done when phase is no longer "expanding"
        if (updated.meta.search_phase !== "expanding") {
          if (expandPollRef.current) clearInterval(expandPollRef.current);
          expandPollRef.current = null;
          setExpanding(false);

          // Reload results
          const r = await fetchAllResults(jobId);
          setAllResults(r);
          refreshCredits();

          // Show toast with count
          const added = updated.meta.expansion_added;
          if (added !== undefined && added > 0) {
            toast.success(t("expansion.addedResults", { count: added }));
          } else if (added === 0) {
            toast.info(t("expansion.noNewResults"));
          }

          if (updated.meta.expansion_error) {
            toast.error(t("expansion.failed"));
          }
        }
      } catch {
        /* ignore poll errors */
      }
    }, 3000);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshCredits is stable from context
  }, [jobId]);

  // Cleanup expansion poll on unmount
  useEffect(() => {
    return () => {
      if (expandPollRef.current) clearInterval(expandPollRef.current);
    };
  }, []);

  useEffect(() => {
    if (!jobId) return;

    let cancelled = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    async function load() {
      try {
        const jobData = await getJobStatus(jobId!);
        if (cancelled) return;
        setJob(jobData);

        if (jobData.status === "completed") {
          const resultsData = await fetchAllResults(jobId!);
          if (cancelled) return;
          setAllResults(resultsData);
          await loadFeedback(jobId!);
          setLoading(false);

          // If page loads with expanding phase, start expansion polling
          if (jobData.meta.search_phase === "expanding") {
            startExpansionPolling();
          }
        } else if (
          jobData.status === "failed" ||
          jobData.status === "cancelled"
        ) {
          setLoading(false);
        } else {
          // Still running — poll
          setLoading(false);
          pollTimer = setInterval(async () => {
            try {
              const updated = await getJobStatus(jobId!);
              if (cancelled) return;
              setJob(updated);
              if (updated.status === "completed") {
                const r = await fetchAllResults(jobId!);
                if (cancelled) return;
                setAllResults(r);
                await loadFeedback(jobId!);
                if (pollTimer) clearInterval(pollTimer);
              } else if (
                updated.status === "failed" ||
                updated.status === "cancelled"
              ) {
                if (pollTimer) clearInterval(pollTimer);
              }
            } catch {
              /* ignore */
            }
          }, 3000);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const { message } = parseApiError(err);
          toast.error(message);
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [jobId, loadFeedback, startExpansionPolling]);

  const filtered = useMemo(() => {
    if (!search) return allResults;
    const q = search.toLowerCase();
    return allResults.filter(
      (r) =>
        r.company_name?.toLowerCase().includes(q) ||
        r.city?.toLowerCase().includes(q) ||
        r.description?.toLowerCase().includes(q) ||
        r.industry_llm?.toLowerCase().includes(q) ||
        r.industry_google?.toLowerCase().includes(q),
    );
  }, [allResults, search]);

  // ── Derived pagination values ──────────────────────────────────
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;

  const pageResults = useMemo(
    () =>
      filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filtered, currentPage],
  );

  // Clamp page when filtered set shrinks (e.g. search filter narrows results)
  // Only clamp after data is loaded (allResults.length > 0) to avoid resetting URL param on initial render
  useEffect(() => {
    if (allResults.length > 0 && currentPage > totalPages) {
      goToPage(Math.max(1, totalPages));
    }
  }, [allResults.length, currentPage, totalPages, goToPage]);

  const handleEnriched = useCallback(
    (resultId: string, enrichmentData: Record<string, unknown>) => {
      setAllResults((prev) =>
        prev.map((r) =>
          r.result_id === resultId
            ? { ...r, enrichment_data: enrichmentData }
            : r,
        ),
      );
    },
    [],
  );

  const toggleExpand = useCallback((resultId: string) => {
    setExpandedResultId((prev) => (prev === resultId ? null : resultId));
  }, []);

  const handleFeedback = useCallback(
    async (
      resultId: string,
      signal: "relevant" | "not_relevant",
      reason?: string,
      note?: string,
    ): Promise<boolean> => {
      if (!jobId) return false;
      try {
        const response = await submitFeedback(jobId, resultId, {
          signal,
          reason: reason as FeedbackReason,
          note: note || null,
        });

        if (response.confidence_score !== undefined) {
          setAllResults((prev) =>
            prev.map((r) =>
              r.result_id === resultId
                ? {
                    ...r,
                    confidence_score: response.confidence_score!,
                    ...(response.confidence_breakdown !== undefined && {
                      confidence_breakdown: response.confidence_breakdown,
                    }),
                  }
                : r,
            ),
          );
        }

        setFeedbackState((prev) => ({
          ...prev,
          [resultId]: { signal, reason },
        }));
        toast.success(
          signal === "relevant"
            ? t("feedback.toastRelevant")
            : t("feedback.toastNotRelevant"),
        );
        return true;
      } catch (err: unknown) {
        console.error("Feedback error:", err);
        return false;
      }
    },
    [jobId, t],
  );

  if (loading) {
    return (
      <div className="px-4 lg:px-8 space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-8 w-full max-w-sm" />
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="rounded-lg border p-4 space-y-2">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-16 w-full" />
            <div className="flex items-center gap-3 mt-3">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-20" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Show progress card for non-completed jobs
  if (job && job.status !== "completed") {
    return (
      <div className="px-4 lg:px-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/jobs")}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> {t("nav.back")}
        </Button>
        <div>
          <RadarSpinner
            status={job.status}
            searchPhase={job.meta.search_phase}
            errorMessage={job.meta.error_message}
            scanned={job.meta.scanned_count}
            target={job.meta.target_count}
            found={job.meta.found_count}
            verified={job.meta.verified_count}
            jobRef={job.job_id.slice(0, 8).toUpperCase()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 lg:px-8">
      {/* ── Header: back | title+params | ml-auto: [CSV, ExpandSearch] ── */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/jobs")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>

        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold tracking-tight">
            {t("page.title")}
          </h1>

          {/* Suchparameter card-style collapsible */}
          {job && (
            <div className="mt-2 border border-border rounded-lg bg-card overflow-hidden">
              <button
                type="button"
                className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-muted/50 transition-colors"
                aria-expanded={paramsOpen}
                aria-controls="suchparameter-panel"
                onClick={() => setParamsOpen(!paramsOpen)}
              >
                {paramsOpen ? (
                  <ChevronUp className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                )}
                <span className="text-xs font-mono tracking-widest uppercase text-muted-foreground">
                  {t("page.searchParamsPrefix")}
                </span>
                {!paramsOpen && (
                  <span className="text-xs text-muted-foreground truncate">
                    {buildSearchSummary(job.request, t)}
                  </span>
                )}
              </button>
              <div
                id="suchparameter-panel"
                hidden={!paramsOpen}
                className="px-3 pb-3 pt-1 flex items-center gap-1.5 flex-wrap"
              >
                {job.request.industry && (
                  <Badge variant="secondary">{job.request.industry}</Badge>
                )}
                {job.request.location && (
                  <Badge variant="secondary">
                    <MapPin className="h-3 w-3 mr-1" />
                    {job.request.location}
                  </Badge>
                )}
                {job.request.employee_size &&
                  job.request.employee_size !== "any" && (
                    <Badge variant="secondary">
                      {job.request.employee_size}
                    </Badge>
                  )}
                {job.request.keywords?.map((k) => (
                  <Badge key={k} variant="outline">
                    {k}
                  </Badge>
                ))}
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground ml-1">
                  <Calendar className="h-3 w-3" />
                  {formatShortDate(job.created_at)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Right-aligned header actions: CSV + Expand search */}
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            disabled={csvExporting}
            onClick={async () => {
              if (!jobId) return;
              setCsvExporting(true);
              try {
                await exportCSV(jobId);
                refreshCredits();
              } catch (err) {
                const { message } = parseApiError(err);
                toast.error(message);
              } finally {
                setCsvExporting(false);
              }
            }}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            {t("toolbar.downloadExcel")}
            {costOf("csv_export") > 0
              ? t("toolbar.downloadExcelCredits", {
                  count: costOf("csv_export"),
                })
              : ""}
          </Button>
          {job && (
            <ExpandSearchDialog
              jobId={jobId!}
              disabled={expanding}
              onExpansionStarted={startExpansionPolling}
            />
          )}
        </div>
      </div>

      {/* ── Toolbar: filter + count + owner buttons ─────────────────── */}
      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder={t("toolbar.filterPlaceholder")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              if (currentPage !== 1) goToPage(1);
            }}
          />
        </div>
        <Badge variant="secondary" className="text-xs">
          {search
            ? t("toolbar.filteredCount", {
                filtered: filtered.length,
                total: allResults.length,
              })
            : t("toolbar.totalCount", { count: allResults.length })}
          {totalPages > 1 &&
            t("toolbar.pageIndicator", {
              current: currentPage,
              total: totalPages,
            })}
        </Badge>
        {job && profile?.is_owner && (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(
                  JSON.stringify(job.request, null, 2),
                );
                toast.success(t("toolbar.copiedJson"));
              }}
            >
              <Copy className="mr-1.5 h-3.5 w-3.5" />
              {t("toolbar.copyJson")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                navigate("/search/new", { state: { prefill: job.request } })
              }
            >
              <Radar className="mr-1.5 h-3.5 w-3.5" />
              {t("toolbar.runAgain")}
            </Button>
          </>
        )}
      </div>

      {/* Expansion progress card */}
      {expanding && (
        <div className="mb-6">
          <RadarSpinner
            status="completed"
            searchPhase="expanding"
            scanned={job?.meta.scanned_count}
            target={job?.meta.target_count}
            found={job?.meta.found_count}
            verified={job?.meta.verified_count}
          />
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState
          icon={Building2}
          title={t("emptyState.title")}
          description={
            search
              ? t("emptyState.descriptionFiltered")
              : t("emptyState.descriptionNoResults")
          }
        />
      ) : (
        <div ref={resultsAreaRef} className="space-y-3">
          {pageResults.map((r) => {
            const isExpanded = expandedResultId === r.result_id;
            return (
              <div key={r.result_id}>
                <ResultCard
                  result={r}
                  expanded={isExpanded}
                  onToggle={toggleExpand}
                  feedbackState={feedbackState[r.result_id]}
                />
                <InlineDetailExpansion
                  result={r}
                  jobId={jobId!}
                  expanded={isExpanded}
                  feedbackState={feedbackState[r.result_id]}
                  onFeedback={handleFeedback}
                  onEnriched={handleEnriched}
                  onClose={() => setExpandedResultId(null)}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <Pagination className="mt-4">
          <PaginationContent>
            <PaginationItem>
              <Button
                size="sm"
                variant="outline"
                disabled={currentPage <= 1}
                onClick={() => goToPage(currentPage - 1)}
              >
                {t("pagination.prev")}
              </Button>
            </PaginationItem>
            <PaginationItem>
              <span className="px-3 text-sm text-muted-foreground">
                {t("pagination.pageOf", {
                  current: currentPage,
                  total: totalPages,
                })}
              </span>
            </PaginationItem>
            <PaginationItem>
              <Button
                size="sm"
                variant="outline"
                disabled={currentPage >= totalPages}
                onClick={() => goToPage(currentPage + 1)}
              >
                {t("pagination.next")}
              </Button>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}
